import { t } from "@travily/i18n";
import type CelebrationsRow from "@travily/types/models/CelebrationsRow";
import { Dialog } from "@ui/components/Dialog";
import { columnsForCount, type SectionCardColumns } from "@ui/components/SectionCard";
import { useClampedText } from "@ui/hooks/use-clamped-text";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

const NS = "components.views.programs.show";

// i18n-tasks-use t('components.views.programs.show.celebrations_title')
// i18n-tasks-use t('components.views.programs.show.celebrations_description')
// i18n-tasks-use t('components.views.programs.show.celebrations_show_all')

/** How many celebrations render in the grid before overflowing into the modal. */
const ABOVE_FOLD_COUNT = 6;

/** Grid cards clamp the quote here so the tilted rows stay height-aligned. */
const GRID_CLAMP_LINES = 3;

const celebrationsCard = tv({
  defaultVariants: { columns: "3" },
  slots: {
    header: "flex flex-col gap-1 xl:gap-2",
    title: "font-extrabold text-2xl text-neutral-900 tracking-2xl xl:text-display-sm xl:tracking-display-sm",
    description: "text-base text-neutral-600 xl:text-xl",
    grid: "grid auto-rows-fr grid-cols-1 gap-2 sm:gap-4",
    showAll: [
      "w-full cursor-pointer rounded-sm text-center font-medium text-neutral-600 text-sm underline xl:text-base",
      "outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 focus-visible:ring-offset-2",
    ],
    card: [
      "relative flex size-full flex-col gap-4 overflow-clip rounded-3xl bg-yellow-50 p-4 text-left",
      "shadow-[inset_0px_-1px_0px_0px_#e2d5b5,inset_0px_0px_0px_1px_#eee2c4]",
    ],
    cardButton:
      "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2",
    cardHeader: "flex items-center gap-2",
    cardName: "min-w-0 truncate font-extrabold text-lg text-yellow-950 leading-6",
    quote: "whitespace-pre-line text-sm text-yellow-800 leading-5",
    // Carries the quote's text metrics so the hook's `lh`-based maxHeight
    // measures the same line height the clamped paragraph renders with.
    clampWrapper: "relative overflow-hidden text-sm leading-5",
    clampOverlay: [
      "absolute right-0 bottom-0 pl-10 after:content-['…']",
      "bg-gradient-to-r from-transparent via-[20%] via-yellow-50 to-yellow-50",
    ],
    dialogRow: "flex gap-4 border-neutral-100 border-b py-3 first:pt-0 last:border-b-0",
    dialogRowName: "font-semibold text-lg text-neutral-900 leading-6",
    dialogRowBody: "whitespace-pre-line text-base text-neutral-600 leading-6",
  },
  variants: {
    // Track count follows the celebration count (shared columnsForCount rule)
    // so one card fills the row and two split it instead of sitting at 1/3.
    columns: {
      "1": {},
      "2": { grid: "sm:grid-cols-2" },
      "3": { grid: "sm:grid-cols-2 xl:grid-cols-3" },
    },
    // Cards tilt only once the grid goes multi-column — the stacked mobile
    // rows render straight (Figma 1418:8836).
    tilt: {
      left: { card: "sm:-rotate-1" },
      right: { card: "sm:rotate-1" },
      none: {},
    },
  },
});

type CelebrationsCardStyles = ReturnType<typeof celebrationsCard>;

function quoted(body: string): string {
  return `“${body}”`;
}

/** The avatar + author-name row shared by grid cards and modal rows. */
function CelebrationAuthor({
  celebration,
  styles,
  tiltAvatar,
}: {
  celebration: CelebrationsRow;
  styles: CelebrationsCardStyles;
  tiltAvatar?: boolean;
}) {
  return (
    <div className={styles.cardHeader()}>
      <span className={tiltAvatar ? "-rotate-2" : undefined}>
        <AccountAvatar
          account={{
            profile_type: celebration.author_profile_type ?? "Family",
            avatar: celebration.author_avatar,
            initial: celebration.author_initial,
          }}
          shape="square"
          size="40"
        />
      </span>
      <span className={styles.cardName()}>{celebration.author_name}</span>
    </div>
  );
}

/**
 * A grid cell: tilt from position, quote clamped to keep the rows height-
 * aligned, and — only once actually clamped — the whole card becomes the
 * trigger that opens the modal scrolled to this celebration.
 */
function GridCelebration({
  celebration,
  columns,
  index,
  onOpen,
}: {
  celebration: CelebrationsRow;
  columns: SectionCardColumns;
  index: number;
  onOpen: (id: number) => void;
}) {
  const { isClamped, ref, style } = useClampedText({ lines: GRID_CLAMP_LINES });
  const tilt = columns === "1" ? "none" : index % 2 === 0 ? "left" : "right";
  const styles = celebrationsCard({ tilt });

  const body = (
    <>
      <CelebrationAuthor celebration={celebration} styles={styles} tiltAvatar={index % 3 === 1} />
      <div className={styles.clampWrapper()} ref={ref} style={style}>
        <p className={styles.quote()}>{quoted(celebration.body)}</p>
        {isClamped && <span aria-hidden className={styles.clampOverlay({ className: styles.quote() })} />}
      </div>
    </>
  );

  return (
    <li>
      {isClamped ? (
        <Button className={styles.card({ className: styles.cardButton() })} onPress={() => onOpen(celebration.id)}>
          {body}
        </Button>
      ) : (
        <div className={styles.card()}>{body}</div>
      )}
    </li>
  );
}

export interface CelebrationsCardProps {
  celebrations: CelebrationsRow[];
  /** The running community's display name, for the section description. */
  communityName: string;
  className?: string;
}

/**
 * "Community Celebrations" program section: what members celebrate about the
 * community running the program (community-wide, not program-specific), as a
 * height-aligned grid of tilted quote cards. Long quotes clamp; a clamped card
 * (and the show-all affordance) opens a modal with every celebration in full,
 * scrolled to the clicked one. Renders nothing without celebrations.
 */
export function CelebrationsCard({ celebrations, communityName, className }: CelebrationsCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const scrollTargetId = useRef<number | null>(null);
  const rowRefs = useRef(new Map<number, HTMLLIElement>());

  const openAt = useCallback((id: number | null) => {
    scrollTargetId.current = id;
    setDialogOpen(true);
  }, []);

  // Scroll the modal list to the card the reader clicked. Double rAF so the
  // sheet's scroller has laid out before the jump; the modal locks the page,
  // so scrollIntoView only moves the dialog body's own scroller.
  useEffect(() => {
    if (!dialogOpen || scrollTargetId.current === null) return;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const id = scrollTargetId.current;
        if (id !== null) rowRefs.current.get(id)?.scrollIntoView({ block: "start" });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [dialogOpen]);

  if (celebrations.length === 0) return null;

  const aboveFold = celebrations.slice(0, ABOVE_FOLD_COUNT);
  const columns = columnsForCount(aboveFold.length);
  const styles = celebrationsCard({ columns });

  return (
    <div className={className}>
      <header className={styles.header()}>
        <h2 className={styles.title()}>{t(`${NS}.celebrations_title`)}</h2>
        <p className={styles.description()}>{t(`${NS}.celebrations_description`, { name: communityName })}</p>
      </header>
      <ul className={styles.grid({ className: "mt-4 xl:mt-10" })}>
        {aboveFold.map((celebration, index) => (
          <GridCelebration
            celebration={celebration}
            columns={columns}
            index={index}
            key={celebration.id}
            onOpen={openAt}
          />
        ))}
      </ul>
      {celebrations.length > ABOVE_FOLD_COUNT && (
        <Button className={styles.showAll({ className: "mt-4" })} onPress={() => openAt(null)}>
          {t(`${NS}.celebrations_show_all`, { count: celebrations.length })}
        </Button>
      )}
      <Dialog.Root isOpen={dialogOpen} onOpenChange={setDialogOpen} size="lg">
        <Dialog.Content>
          <Dialog.Header title={t(`${NS}.celebrations_title`)} />
          <Dialog.Body>
            <ul>
              {celebrations.map((celebration) => (
                <li
                  className={styles.dialogRow()}
                  key={celebration.id}
                  ref={(element) => {
                    if (element) rowRefs.current.set(celebration.id, element);
                    else rowRefs.current.delete(celebration.id);
                  }}
                >
                  <span className="shrink-0">
                    <AccountAvatar
                      account={{
                        profile_type: celebration.author_profile_type ?? "Family",
                        avatar: celebration.author_avatar,
                        initial: celebration.author_initial,
                      }}
                      shape="square"
                      size="40"
                    />
                  </span>
                  <div className="flex flex-col gap-1">
                    <h3 className={styles.dialogRowName()}>{celebration.author_name}</h3>
                    <p className={styles.dialogRowBody()}>{quoted(celebration.body)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
